'use client'

import React, { forwardRef, useState, ComponentProps } from 'react'

import { Input } from '@dc-shared-web/components/shadcn/input'
import { Chip } from '@dc-shared-web/components/base/Chip'

import { cn } from '@dc-shared-web/lib'
import { preventDefault } from '@dc-shared-web/lib/utils'

type MultiInputProps = {
  values: string[]
  onChange: (values: string[]) => void
  sanitizer?: (value: string) => string
  splitRegexp?: RegExp
  placeholder?: string
  isError?: boolean
  containerClassName?: string
  inputProps?: Omit<
    ComponentProps<typeof Input>,
    | 'placeholder'
    | 'value'
    | 'onChange'
    | 'onKeyDown'
    | 'onPaste'
    | 'onFocus'
    | 'onBlur'
  >
}

const defaultSanitizer = (value: string) => value.replace(/[,;\s]/g, '')

const defaultSplitRegexp = /[,;\s]/

const MultiInput = (
  {
    values,
    onChange,
    sanitizer = defaultSanitizer,
    splitRegexp = defaultSplitRegexp,
    placeholder,
    isError,
    containerClassName,
    inputProps,
  }: MultiInputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const [currentValue, setCurrentValue] = useState('')
  const [focused, setFocused] = useState(false)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const newValue = sanitizer(currentValue)

    if (e.key === 'Backspace' && !newValue) {
      onChange(values.slice(0, -1))
    } else if (e.key === 'Enter' || e.key.match(splitRegexp)) {
      if (newValue) {
        onChange([...values, currentValue])
        setCurrentValue('')
      }
      preventDefault(e)
    }
  }

  const handleChipRemoval = (value: string) => {
    onChange(values.filter((val) => val !== value))
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = e.clipboardData.getData('text')
    const clipboardValues = clipboardData
      .split(splitRegexp)
      .filter((v) => sanitizer(v))
    const uniqueValues = Array.from(new Set([...values, ...clipboardValues]))
    onChange(uniqueValues)
    preventDefault(e)
  }

  const handleFocusOut = () => {
    if (sanitizer(currentValue)) {
      onChange([...values, currentValue])
      setCurrentValue('')
    }
    setFocused(false)
  }

  const { className, inputClassName, ...forwardedInputProps } = inputProps ?? {}

  return (
    <div
      className={cn(
        'tw-border-[1px] tw-rounded-md tw-bg-background tw-border-neutral-200 tw-flex tw-flex-wrap tw-p-2 tw-gap-y-1',
        focused && 'tw-ring-2 tw-ring-ring',
        isError && 'tw-border-red-600 tw-ring-0',
        containerClassName
      )}
    >
      {values?.map((val, index) => (
        <Chip
          key={index}
          value={{ value: val, label: val }}
          onRemove={handleChipRemoval}
        />
      ))}
      <Input
        ref={ref}
        className={cn('tw-grow', className)}
        inputClassName={cn(
          'tw-border-0 tw-ring-inset focus-visible:tw-outline-transparent tw-px-0 tw-py-0 hover:tw-bg-white focus-visible:tw-bg-white focus-visible:tw-ring-0 placeholder:tw-text-neutral-500',
          inputClassName
        )}
        placeholder={placeholder ?? ''}
        value={currentValue}
        onChange={(e) => setCurrentValue(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={handleFocusOut}
        {...forwardedInputProps}
      />
    </div>
  )
}

MultiInput.displayName = 'MultiInput'
export default forwardRef(MultiInput)
